import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LogOut, ChevronRight, Crown, Bell,
  Users, QrCode, LifeBuoy, Activity,
} from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { householdsApi, notificationsApi } from "../lib/api";
import { unreadAlertCount } from "../lib/viewedAlerts";
import { PremiumModal } from "../components/PremiumLockTile";
import { Avatar } from "../components/Avatar";
import { AirbnbAvatarStack } from "../components/FamilyAvatarBubbles";

// Single reusable row button — icon left, content middle, trailing right, chevron last.
const ProfileRow = ({ icon: Icon, label, trailing, onClick, testid, iconBg = "bg-[#DCEDF0]", iconFg = "text-[#1F5566]" }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="w-full flex items-center gap-3 bg-white border border-[#E2E0D8] rounded-2xl px-4 py-3.5 hover:bg-[#F5F3ED] transition-colors text-left"
  >
    <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
      <Icon className={`w-5 h-5 ${iconFg}`} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-heading text-base font-semibold text-[#2D3142] truncate">{label}</div>
    </div>
    {trailing && <div className="shrink-0 text-xs text-[#8A93A6] flex items-center gap-1.5">{trailing}</div>}
    <ChevronRight className="w-4 h-4 text-[#8A93A6] shrink-0" />
  </button>
);

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [household, setHousehold] = useState(null);
  const [alertCount, setAlertCount] = useState(0);
  const [premiumOpen, setPremiumOpen] = useState(false);

  const householdId = user?.household_id;

  const loadHousehold = useCallback(async () => {
    if (!householdId) { setHousehold(null); return; }
    try {
      const h = await householdsApi.me();
      setHousehold(h);
    } catch (err) {
      console.error("Failed to load household:", err);
      setHousehold(null);
    }
  }, [householdId]);

  useEffect(() => { loadHousehold(); }, [loadHousehold]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const f = await notificationsApi.feed();
        if (cancelled) return;
        // Mirror Dashboard's behavior: only count alerts the user hasn't
        // marked as viewed yet. Keeps the two badges in sync.
        setAlertCount(f.enabled ? unreadAlertCount(user?.id, f.alerts) : 0);
      } catch (err) {
        console.warn("Failed to load notifications feed:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.notification_prefs]);

  const handleLogout = async () => {
    await logout();
    navigate("/auth");
  };

  const memberCount = household?.members?.length || 0;
  const notifsEnabled = user?.notification_prefs?.enabled !== false;

  return (
    <div className="px-5 pt-6 pb-4">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#8A93A6]">Profile</div>
        <h1 className="font-heading text-3xl font-bold text-[#2D3142] mt-1">Your account</h1>
      </motion.div>

      {/* User card — entire tile is now a button that opens /settings, where
          the user can edit their avatar and name. We intentionally pass
          `editable={false}` so no add/delete-photo buttons appear here. */}
      <button
        type="button"
        onClick={() => navigate("/settings")}
        data-testid="profile-user-card"
        className="mt-6 w-full tup-card p-5 text-left hover:bg-[#F5F3ED] transition-colors"
      >
        <div className="flex items-center gap-4">
          <Avatar
            user={user}
            size={64}
            editable={false}
            testId="profile-avatar"
          />
          <div className="flex-1 min-w-0">
            <div className="font-heading text-xl font-semibold text-[#2D3142] truncate">
              {user?.name}
            </div>
            <div className="text-sm text-[#8A93A6] truncate mt-0.5">{user?.email}</div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#8A93A6] shrink-0" />
        </div>
      </button>

      {/* Row buttons */}
      <div className="mt-5 space-y-2.5">
        <ProfileRow
          icon={Crown}
          iconBg={user?.is_premium ? "brand-gradient-bg" : "bg-[#F5F3ED]"}
          iconFg={user?.is_premium ? "text-white" : "text-[#8A93A6]"}
          label={user?.is_premium ? "Premium plan" : "Free plan"}
          trailing={
            user?.is_premium ? (
              <span
                data-testid="premium-active-badge"
                className="text-[10px] uppercase tracking-wider font-bold brand-gradient-bg text-white px-2 py-1 rounded-full"
              >
                {user?.premium_until
                  ? `Renews ${new Date(user.premium_until).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
                  : "Active"}
              </span>
            ) : (
              <span
                className="font-semibold brand-gradient-text"
                data-testid="upgrade-plan-cta"
              >
                Upgrade Plan
              </span>
            )
          }
          onClick={() => setPremiumOpen(true)}
          testid="profile-subscription-btn"
        />

        <button
          type="button"
          onClick={() => navigate(user?.household_id ? "/household" : "/onboarding")}
          data-testid="profile-household-btn"
          className="w-full flex items-center gap-3 bg-white border border-[#E2E0D8] rounded-2xl px-4 py-3.5 hover:bg-[#F5F3ED] transition-colors text-left"
        >
          <div className="w-10 h-10 rounded-xl bg-[#DCEDF0] flex items-center justify-center shrink-0">
            <Users className="w-5 h-5 text-[#1F5566]" />
          </div>
          {/* "Household" sits flush against "3 members" — much tighter than
              the old layout — and the Airbnb-style stack fills the trailing
              space on the right. Mirrors the right-side mockup the user shared. */}
          <div className="flex-1 min-w-0 flex items-baseline gap-2">
            <span className="font-heading text-base font-semibold text-[#2D3142]">
              Household
            </span>
            {user?.household_id ? (
              <span
                className="text-xs text-[#8A93A6] truncate"
                data-testid="profile-household-count"
              >
                {household ? `${memberCount} ${memberCount === 1 ? "member" : "members"}` : "…"}
              </span>
            ) : (
              <span className="text-xs text-[#E07A5F] font-semibold">Set up</span>
            )}
          </div>
          {user?.household_id && household && (
            <AirbnbAvatarStack
              currentUser={user}
              members={household.members || []}
              size={28}
              maxVisible={3}
              testid="profile-household-stack"
            />
          )}
          <ChevronRight className="w-4 h-4 text-[#8A93A6] shrink-0" />
        </button>

        <ProfileRow
          icon={Bell}
          label="Notifications"
          trailing={
            !notifsEnabled ? (
              <span>Off</span>
            ) : alertCount > 0 ? (
              <span
                data-testid="profile-alert-badge"
                className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#E07A5F] text-white text-[10px] font-bold flex items-center justify-center"
              >
                {alertCount > 9 ? "9+" : alertCount}
              </span>
            ) : (
              <span>On</span>
            )
          }
          onClick={() => navigate("/notifications")}
          testid="profile-notifications-btn"
        />

        <ProfileRow
          icon={QrCode}
          label="QR Labels"
          trailing={<span className="text-[#277A82] font-semibold">Shop · Print</span>}
          onClick={() => navigate("/qr-labels")}
          testid="profile-qr-labels-btn"
        />

        <ProfileRow
          icon={LifeBuoy}
          label="Help & Support"
          onClick={() => navigate("/help")}
          testid="profile-help-btn"
        />

        {/* Operator-only — backend gates the data, frontend just hides the
            row from non-admin emails so the menu stays clean. The actual
            authorization happens server-side regardless of this check. */}
        {(user?.email || "").toLowerCase() === "bravestglint@gmail.com" && (
          <ProfileRow
            icon={Activity}
            iconBg="bg-[#FBE8DA]"
            iconFg="text-[#B27300]"
            label="App health"
            trailing={<span className="text-[#B27300] font-semibold">Admin</span>}
            onClick={() => navigate("/admin/health")}
            testid="profile-admin-health-btn"
          />
        )}
      </div>

      <button
        onClick={handleLogout}
        data-testid="logout-btn"
        className="w-full mt-6 flex items-center justify-center gap-2 text-sm text-[#4F5D75] hover:text-[#9C3E1D] py-3"
      >
        <LogOut className="w-4 h-4" /> Sign out
      </button>

      <PremiumModal open={premiumOpen} onClose={() => setPremiumOpen(false)} />
    </div>
  );
};

export default Profile;
